import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    listarAgendaTurnosAnses,
    listarTurnosAnsesPorResultado,
    crearTurnoAnses,
    editarTurnoAnses,
    cancelarTurnoAnses,
    eliminarTurnoAnses
} from '../../services/turnoAnsesService';
import ModalDetalleTurnoAnses from '../../components/ModalDetalleTurnoAnses';
import ModalConfirmacionCambioEstado from '../../components/ModalConfirmacionCambioEstado';
import { getTipoTramites } from '../../services/tipoTramiteService';
import { buscarClientes } from '../../services/clienteService';
import DatosMaestrosService from '../../services/datosMaestrosService';
import { usuarioService } from '../../services/usuarioService';
import '../../style/pages/HomePage.css';
import '../../style/pages/TurnosAnses.css';
import useBreakpoint from '../../hooks/useBreakpoint';
import BottomSheet from '../../components/mobile/BottomSheet';
import BotonFiltros from '../../components/mobile/BotonFiltros';
import ChipsFiltros from '../../components/mobile/ChipsFiltros';
import { apellidoNombre } from '../../utils/nombrePersona';
import { TIPOS_TURNO_ANSES, esSinPresentacion, metaTipoTurnoAnses } from '../../utils/tiposTurnoAnses';
import { coincideBusqueda } from '../../utils/textoComparable';

const hoyISO = () => {
    const ahora = new Date();
    return new Date(ahora.getTime() - ahora.getTimezoneOffset() * 60000).toISOString().split('T')[0];
};

const sumarDias = (fechaISO, dias) => {
    const fecha = new Date(`${fechaISO}T00:00:00`);
    fecha.setDate(fecha.getDate() + dias);
    return new Date(fecha.getTime() - fecha.getTimezoneOffset() * 60000).toISOString().split('T')[0];
};

const formatearDia = (fechaISO) => {
    if (!fechaISO) return '';
    const fecha = new Date(`${String(fechaISO).split('T')[0]}T00:00:00`);
    if (Number.isNaN(fecha.getTime())) return fechaISO;
    const texto = fecha.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
    return texto.charAt(0).toUpperCase() + texto.slice(1);
};

// Los 7 valores del enum ResultadoPresentacion. Antes faltaban los dos de cierre, asi que un
// tramite resuelto a favor caia en el fallback y se mostraba "Pendiente" en celeste.
const RESULTADO_META = {
    PENDIENTE: { label: 'Pendiente', badge: 'bg-info' },
    INGRESADO: { label: 'Ingresado', badge: 'bg-primary' },
    CONDICIONAL: { label: 'Condicional', badge: 'bg-warning text-dark' },
    RECHAZADO: { label: 'Rechazado', badge: 'bg-danger' },
    NO_PRESENTADO: { label: 'No presentado', badge: 'bg-secondary' },
    FAVORABLE: { label: 'Favorable', badge: 'bg-success' },
    DESFAVORABLE: { label: 'Desfavorable', badge: 'bg-danger' }
};

const metaResultado = (resultado) => RESULTADO_META[resultado] || RESULTADO_META.PENDIENTE;

// Ruta de la ficha del trámite según su tipo, para poder abrirla desde la agenda.
const RUTA_POR_TIPO = {
    RTI: '/rti/tramites',
    ART: '/art/tramites',
    JUBILACION: '/jubilacion/tramites',
    PUAM: '/puam/tramites',
    PLAN_PAGO: '/plan-pago/tramites',
    REAJUSTE: '/reajuste/tramites',
    RECONOCIMIENTO: '/reconocimiento/tramites',
    PNC: '/pnc/tramites',
    AMPARO_PNC: '/amparo-pnc/tramites',
    AMPARO_LEY_16986: '/amparo-ley-16986/tramites',
    GENERICO: '/generico/tramites',
    GANANCIAS: '/ganancias/tramites',
    MINUSVALIA: '/minusvalia/tramites'
};

const rutaDelTramite = (presentacion) => {
    if (!presentacion?.idTramite) return null;
    const base = RUTA_POR_TIPO[String(presentacion.tipoTramite || '').trim().toUpperCase()];
    return base ? `${base}/${presentacion.idTramite}` : null;
};

const formularioVacio = () => ({
    cuil: '',
    nombreCliente: '',
    idTipoTramite: '',
    fecha: hoyISO(),
    hora: '',
    tipo: 'PRESENTACION',
    tieneTurnoOficial: true,
    udaiLugar: '',
    offsetMinutos: '',
    requiereFirma: false,
    documentacionAFirmar: '',
    idUsuarioApoderado: '',
    apoderadoNombre: '',
    apoderadoDni: '',
    apoderadoRelacion: '',
    observaciones: '',
    horaEscribania: '',
    escribaniaObservacion: ''
});

/**
 * Módulo de Turnos ANSES: la agenda propia del estudio.
 *
 * Muestra TODAS las idas, incluidas las que van con poder de un abogado del estudio y por eso no
 * ocupan lugar en el turnero. Permite cargar un turno sin que exista la carpeta del trámite
 * (alcanza con el cliente y el tipo), que es lo que habilita usar esto antes de que los módulos
 * de trámites estén en producción.
 */
function TurnosAnsesPage() {
    const navigate = useNavigate();
    const [turnos, setTurnos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState('');

    const [filtros, setFiltros] = useState({
        desde: hoyISO(),
        hasta: sumarDias(hoyISO(), 30),
        estado: '',
        soloCondicionales: false
    });
    // Filtro de citación: se resuelve en el cliente, sobre lo que ya trajo la agenda. Lo que
    // interesa en la práctica es la lista de a quiénes falta avisarles.
    const [filtroCitacion, setFiltroCitacion] = useState('');

    const [tiposTramite, setTiposTramite] = useState([]);
    const [lugares, setLugares] = useState([]);
    const [usuarios, setUsuarios] = useState([]);

    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    const [mostrarAlta, setMostrarAlta] = useState(false);
    const [detalle, setDetalle] = useState(null);
    const [turnoAEliminar, setTurnoAEliminar] = useState(null);
    // La edicion reutiliza el formulario del alta: es el mismo juego de campos, y el backend ya
    // tenia el PUT — solo faltaba una forma de llamarlo desde el listado.
    const [editando, setEditando] = useState(null);
    const [filtroTexto, setFiltroTexto] = useState('');
    const [datos, setDatos] = useState(formularioVacio());
    const [guardando, setGuardando] = useState(false);
    const [busquedaCliente, setBusquedaCliente] = useState('');
    const [resultadosCliente, setResultadosCliente] = useState([]);
    const [buscandoCliente, setBuscandoCliente] = useState(false);
    const debounceBusqueda = useRef(null);

    const cargar = useCallback(async () => {
        setCargando(true);
        setError('');
        try {
            const datosAgenda = filtros.soloCondicionales
                ? await listarTurnosAnsesPorResultado('CONDICIONAL')
                : await listarAgendaTurnosAnses({
                    desde: filtros.desde,
                    hasta: filtros.hasta,
                    estado: filtros.estado || undefined
                });
            setTurnos(datosAgenda);
        } catch (e) {
            setError(e?.response?.data?.mensaje || 'No se pudieron cargar los turnos ANSES.');
        } finally {
            setCargando(false);
        }
    }, [filtros]);

    useEffect(() => { cargar(); }, [cargar]);

    // Catálogos: solo cuando se abre el alta, para no pedirlos en cada carga de la agenda.
    useEffect(() => {
        if (!mostrarAlta) return;
        let cancelado = false;
        (async () => {
            try {
                const [tipos, respuestaLugares, respuestaUsuarios] = await Promise.all([
                    getTipoTramites(),
                    DatosMaestrosService.getLugares(),
                    usuarioService.getParaSeleccion()
                ]);
                if (cancelado) return;
                setTiposTramite(Array.isArray(tipos) ? tipos : []);
                setLugares(Array.isArray(respuestaLugares.data) ? respuestaLugares.data : []);
                setUsuarios(Array.isArray(respuestaUsuarios.data) ? respuestaUsuarios.data : []);
            } catch (e) {
                console.error('Error al cargar catálogos:', e);
            }
        })();
        return () => { cancelado = true; };
    }, [mostrarAlta]);

    // La escribanía solo existe en reajuste: es el paso de firmar la documental después de
    // ANSES. En los demás tipos el backend descarta el dato, así que tampoco se pide.
    const esReajusteElegido = useMemo(() => {
        const tipo = tiposTramite.find(t => String(t.idTipoTramite) === String(datos.idTipoTramite));
        return (tipo?.nombre || '').trim().toUpperCase() === 'REAJUSTE';
    }, [tiposTramite, datos.idTipoTramite]);

    const turnosPorDia = useMemo(() => {
        // Sin tildes y por palabras: ver coincideBusqueda.
        const coincide = (t) => coincideBusqueda(
            [t.apellidoCliente, t.nombreCliente, t.cuil, t.udaiLugar], filtroTexto);
        const coincideCitacion = (t) => {
            if (!filtroCitacion) return true;
            // "Sin citación registrada" no afirma que no esté citado: dice que en el sistema no
            // hay registro. Es la lista de a quiénes conviene llamar.
            return filtroCitacion === 'CITADO' ? !!t.citado : !t.citado;
        };
        const agrupados = new Map();
        turnos.filter(t => coincide(t) && coincideCitacion(t)).forEach(turno => {
            const dia = String(turno.fecha).split('T')[0];
            if (!agrupados.has(dia)) agrupados.set(dia, []);
            agrupados.get(dia).push(turno);
        });
        return Array.from(agrupados.entries()).sort(([a], [b]) => a.localeCompare(b));
    }, [turnos, filtroTexto, filtroCitacion]);

    // Búsqueda en vivo mientras se escribe, con el mismo debounce de 350ms que usa
    // SelectorReferenciaCliente. Sin botón: se busca solo.
    useEffect(() => {
        if (debounceBusqueda.current) clearTimeout(debounceBusqueda.current);
        if (!busquedaCliente.trim()) {
            setResultadosCliente([]);
            setBuscandoCliente(false);
            return undefined;
        }
        setBuscandoCliente(true);
        debounceBusqueda.current = setTimeout(async () => {
            try {
                const encontrados = await buscarClientes(busquedaCliente.trim());
                setResultadosCliente(Array.isArray(encontrados) ? encontrados.slice(0, 8) : []);
            } catch (e) {
                setResultadosCliente([]);
            } finally {
                setBuscandoCliente(false);
            }
        }, 350);
        return () => clearTimeout(debounceBusqueda.current);
    }, [busquedaCliente]);

    const handleCrear = async () => {
        if (!datos.cuil) {
            toast.error('Elegí el cliente del turno.');
            return;
        }
        // Solo al crear: la edición no toca las presentaciones, que ya existen y se manejan
        // desde el trámite. Pedirlo acá era pedir un dato que después se ignora.
        if (!editando && !datos.idTipoTramite) {
            toast.error('Indicá qué tipo de trámite se va a presentar.');
            return;
        }
        if (!datos.fecha || !datos.hora) {
            toast.error('La fecha y la hora son obligatorias.');
            return;
        }

        setGuardando(true);
        try {
            if (editando) {
                const actualizado = await editarTurnoAnses(editando.idTurnoAnses, {
                    fecha: datos.fecha,
                    horaAnses: datos.hora,
                    tieneTurnoOficial: datos.tieneTurnoOficial,
                    udaiLugar: datos.udaiLugar || null,
                    offsetMinutos: datos.offsetMinutos === '' ? null : Number(datos.offsetMinutos),
                    requiereFirma: datos.requiereFirma,
                    documentacionAFirmar: datos.documentacionAFirmar || null,
                    idUsuarioApoderado: datos.idUsuarioApoderado === '' ? null : Number(datos.idUsuarioApoderado),
                    apoderadoNombre: datos.apoderadoNombre || null,
                    apoderadoDni: datos.apoderadoDni || null,
                    apoderadoRelacion: datos.apoderadoRelacion || null,
                    observaciones: datos.observaciones || null
                });
                if (actualizado?.avisoCitacion) toast.info(actualizado.avisoCitacion, { autoClose: 8000 });
                toast.success('Turno actualizado.');
                setMostrarAlta(false);
                setEditando(null);
                setDatos(formularioVacio());
                cargar();
                return;
            }
            const creado = await crearTurnoAnses({
                cuil: datos.cuil,
                presentaciones: [{ idTipoTramite: Number(datos.idTipoTramite) }],
                fecha: datos.fecha,
                horaAnses: datos.hora,
                tipo: datos.tipo,
                tieneTurnoOficial: datos.tieneTurnoOficial,
                udaiLugar: datos.udaiLugar || null,
                offsetMinutos: datos.offsetMinutos === '' ? null : Number(datos.offsetMinutos),
                requiereFirma: datos.requiereFirma,
                documentacionAFirmar: datos.documentacionAFirmar || null,
                idUsuarioApoderado: datos.idUsuarioApoderado === '' ? null : Number(datos.idUsuarioApoderado),
                apoderadoNombre: datos.apoderadoNombre || null,
                apoderadoDni: datos.apoderadoDni || null,
                apoderadoRelacion: datos.apoderadoRelacion || null,
                observaciones: datos.observaciones || null,
                horaEscribania: esReajusteElegido ? (datos.horaEscribania || null) : null,
                escribaniaObservacion: esReajusteElegido ? (datos.escribaniaObservacion || null) : null
            });
            if (creado?.avisoCitacion) toast.info(creado.avisoCitacion, { autoClose: 8000 });
            toast.success('Turno ANSES cargado.');
            setMostrarAlta(false);
            setDatos(formularioVacio());
            setResultadosCliente([]);
            setBusquedaCliente('');
            cargar();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo cargar el turno.');
        } finally {
            setGuardando(false);
        }
    };

    const abrirEdicion = (turno) => {
        setDatos({
            ...formularioVacio(),
            cuil: turno.cuil,
            nombreCliente: `${turno.apellidoCliente}, ${turno.nombreCliente}`,
            fecha: String(turno.fecha).split('T')[0],
            hora: turno.tieneTurnoOficial ? turno.horaAnses : turno.horaCitacionEstudio,
            tipo: turno.tipo || 'PRESENTACION',
            tieneTurnoOficial: turno.tieneTurnoOficial,
            udaiLugar: turno.udaiLugar || '',
            offsetMinutos: turno.offsetMinutos ?? '',
            requiereFirma: !!turno.requiereFirma,
            documentacionAFirmar: turno.documentacionAFirmar || '',
            idUsuarioApoderado: turno.idUsuarioApoderado ?? '',
            apoderadoNombre: turno.apoderadoNombre || '',
            apoderadoDni: turno.apoderadoDni || '',
            apoderadoRelacion: turno.apoderadoRelacion || '',
            observaciones: turno.observaciones || ''
        });
        setEditando(turno);
        setMostrarAlta(true);
    };

    // Es irreversible y el turno ya está cancelado, así que se pide confirmación explícita
    // con el modal del sistema (antes usaba window.confirm, que rompe la estética del resto
    // de la app).
    const handleEliminar = (turno) => setTurnoAEliminar(turno);

    const confirmarEliminar = async () => {
        if (!turnoAEliminar) return;
        setGuardando(true);
        try {
            await eliminarTurnoAnses(turnoAEliminar.idTurnoAnses);
            toast.success('Turno eliminado.');
            setTurnoAEliminar(null);
            cargar();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo eliminar el turno.');
        } finally {
            setGuardando(false);
        }
    };

    const handleCancelar = async (turno) => {
        setGuardando(true);
        try {
            await cancelarTurnoAnses(turno.idTurnoAnses, null);
            toast.success('Turno cancelado.');
            cargar();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo cancelar el turno.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div className="homepage-container">
            <ToastContainer position="top-right" autoClose={4000} />

            <div className="homepage-header">
                <div>
                    <h1 className="homepage-title">
                        <i className="bi bi-building me-3"></i>
                        Turnos ANSES
                    </h1>
                    <p className="homepage-subtitle">
                        Agenda de los turnos de ANSES. Incluye los que van con poder de un abogado del
                        estudio, que no ocupan lugar en el turnero.
                    </p>
                </div>
            </div>

            {/* Filtros */}
            <div className="anses-filtros">
                {(() => {
                    const campoBuscar = (
                        <>
                            <Form.Label className="anses-filtros-label">Buscar</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Nombre, apellido, CUIL o UDAI…"
                                value={filtroTexto}
                                onChange={(e) => setFiltroTexto(e.target.value)}
                            />
                        </>
                    );
                    const campoDesde = (
                        <>
                            <Form.Label className="anses-filtros-label">Desde</Form.Label>
                            <Form.Control
                                type="date"
                                value={filtros.desde}
                                disabled={filtros.soloCondicionales}
                                onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })}
                            />
                        </>
                    );
                    const campoHasta = (
                        <>
                            <Form.Label className="anses-filtros-label">Hasta</Form.Label>
                            <Form.Control
                                type="date"
                                value={filtros.hasta}
                                disabled={filtros.soloCondicionales}
                                onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })}
                            />
                        </>
                    );
                    const campoEstado = (
                        <>
                            <Form.Label className="anses-filtros-label">Estado del turno</Form.Label>
                            <Form.Select
                                value={filtros.estado}
                                disabled={filtros.soloCondicionales}
                                onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
                            >
                                <option value="">Todos</option>
                                <option value="PROGRAMADO">Programado</option>
                                <option value="REALIZADO">Realizado</option>
                                <option value="CANCELADO">Cancelado</option>
                            </Form.Select>
                        </>
                    );
                    const campoCitacion = (
                        <>
                            <Form.Label className="anses-filtros-label">Citación al cliente</Form.Label>
                            <Form.Select value={filtroCitacion} onChange={(e) => setFiltroCitacion(e.target.value)}>
                                <option value="">Todos</option>
                                <option value="CITADO">Citados</option>
                                <option value="SIN_REGISTRO">Sin citación registrada</option>
                            </Form.Select>
                        </>
                    );

                    const chipsFiltros = [
                        filtroTexto && { key: 'texto', label: filtroTexto, onQuitar: () => setFiltroTexto('') },
                        (!filtros.soloCondicionales && filtros.estado) && {
                            key: 'estado',
                            label: filtros.estado === 'PROGRAMADO' ? 'Programado' : filtros.estado === 'REALIZADO' ? 'Realizado' : 'Cancelado',
                            onQuitar: () => setFiltros({ ...filtros, estado: '' }),
                        },
                        filtroCitacion && { key: 'citacion', label: filtroCitacion === 'CITADO' ? 'Citados' : 'Sin citación registrada', onQuitar: () => setFiltroCitacion('') },
                    ].filter(Boolean);

                    return esMobile ? (
                        <>
                            <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                            <ChipsFiltros chips={chipsFiltros} />
                            {sheetFiltrosAbierto && (
                                <BottomSheet
                                    titulo="Filtros"
                                    onCerrar={() => setSheetFiltrosAbierto(false)}
                                    accionPrimaria={chipsFiltros.length > 0 ? {
                                        label: 'Limpiar filtros',
                                        onClick: () => { setFiltroTexto(''); setFiltros({ ...filtros, estado: '' }); setFiltroCitacion(''); },
                                    } : undefined}
                                >
                                    <div className="mb-3">{campoBuscar}</div>
                                    <div className="mb-3">{campoDesde}</div>
                                    <div className="mb-3">{campoHasta}</div>
                                    <div className="mb-3">{campoEstado}</div>
                                    {campoCitacion}
                                </BottomSheet>
                            )}
                        </>
                    ) : (
                        <Row className="g-3 align-items-end">
                            <Col md={12}>{campoBuscar}</Col>
                            <Col md={4}>{campoDesde}</Col>
                            <Col md={4}>{campoHasta}</Col>
                            <Col md={4}>{campoEstado}</Col>
                            <Col md={4}>{campoCitacion}</Col>
                        </Row>
                    );
                })()}

                <div className="anses-barra-acciones">
                    {/* Limpiar editando/datos antes de abrir: si venías de editar un turno, el
                        modal se reabría en modo edición (título "Editar turno ANSES", sin el
                        selector "¿A qué se va?") y guardar habría pisado ESE turno en vez de
                        crear uno nuevo. */}
                    <Button variant="primary" className="rounded-pill px-4"
                            onClick={() => { setEditando(null); setDatos(formularioVacio()); setMostrarAlta(true); }}>
                        <i className="bi bi-plus-lg me-2"></i>Cargar turno
                    </Button>

                    {/* Los que esperan que ANSES avise si los toma. */}
                    <Button
                        variant={filtros.soloCondicionales ? 'warning' : 'outline-warning'}
                        className="rounded-pill px-4"
                        onClick={() => setFiltros({ ...filtros, soloCondicionales: !filtros.soloCondicionales })}
                    >
                        <i className="bi bi-hourglass-split me-2"></i>
                        {filtros.soloCondicionales ? 'Volver a la agenda' : 'Mostrar condicionales'}
                    </Button>

                    {filtros.soloCondicionales && (
                        <small className="text-muted">
                            Presentaciones que esperan que ANSES avise si las toma.
                        </small>
                    )}
                </div>
            </div>

            {cargando ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 mb-0 text-muted">Cargando turnos...</p>
                </div>
            ) : error ? (
                <div className="alert alert-danger border-0 rounded-3">{error}</div>
            ) : turnosPorDia.length === 0 ? (
                <div className="alert alert-info border-0 rounded-3">
                    {filtros.soloCondicionales
                        ? 'No hay presentaciones esperando respuesta de ANSES.'
                        : 'No hay turnos ANSES en el período elegido.'}
                </div>
            ) : (
                turnosPorDia.map(([dia, turnosDelDia]) => (
                    <div key={dia}>
                        <div className="anses-dia">
                            <h5 className="anses-dia-titulo">{formatearDia(dia)}</h5>
                            <Badge bg="light" text="dark">{turnosDelDia.length}</Badge>
                            <span className="anses-dia-linea" />
                        </div>

                        {turnosDelDia.map(turno => (
                            <div
                                key={turno.idTurnoAnses}
                                className={`anses-turno ${turno.estado === 'CANCELADO' ? 'anses-turno-cancelado' : ''} ${turno.estado === 'REALIZADO' ? 'anses-turno-realizado' : ''}`}
                            >
                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                                    <div className="d-flex gap-3">
                                        <div className="text-center">
                                            <div className="anses-turno-hora">
                                                {turno.tieneTurnoOficial ? turno.horaAnses : turno.horaCitacionEstudio}
                                            </div>
                                            <div className="anses-turno-hora-sub">
                                                {turno.tieneTurnoOficial ? 'en ANSES' : 'en el estudio'}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                <span className="anses-turno-nombre">
                                                    {turno.apellidoCliente}, {turno.nombreCliente}
                                                </span>
                                                {turno.estado === 'CANCELADO' && <Badge bg="secondary">Cancelado</Badge>}
                                                {turno.estado === 'REALIZADO' && <Badge bg="success">Realizado</Badge>}
                                                {esSinPresentacion(turno.tipo) && (
                                                    <Badge bg="primary">
                                                        <i className={`bi ${metaTipoTurnoAnses(turno.tipo).icono} me-1`}></i>
                                                        {metaTipoTurnoAnses(turno.tipo).badge}
                                                    </Badge>
                                                )}
                                                {!esSinPresentacion(turno.tipo) && !turno.tieneTurnoOficial && (
                                                    <Badge bg="light" text="dark">Sin turno del organismo</Badge>
                                                )}
                                                {turno.usuarioApoderadoNombre && (
                                                    <Badge bg="info">Poder: {turno.usuarioApoderadoNombre}</Badge>
                                                )}
                                                {turno.requiereFirma && !turno.firmaResuelta && (
                                                    <Badge bg="warning" text="dark">Falta firmar</Badge>
                                                )}
                                                {/* El chip aparece recién cuando se registró la
                                                    citación. Sin registro no se muestra nada: los
                                                    turnos citados por fuera del sistema no tienen
                                                    por qué figurar como pendientes. */}
                                                {turno.citado && (
                                                    <Badge bg="success">
                                                        <i className="bi bi-telephone me-1"></i>Citado
                                                    </Badge>
                                                )}
                                            </div>

                                            <div className="anses-turno-meta">
                                                {turno.tieneTurnoOficial && <>Turno en el estudio {turno.horaCitacionEstudio} · </>}
                                                {turno.udaiLugar || 'Sin UDAI'}
                                                {turno.intento > 1 ? ` · intento ${turno.intento}°` : ''}
                                                {/* De parte de quién viene: es lo que el estudio mira para saber
                                                    cómo tratarlo, y hasta ahora no se veía en el listado. */}
                                                {turno.referenciaCliente && <> · Ref: {turno.referenciaCliente}</>}
                                                {turno.horaEscribania && (
                                                    <> · <span className="anses-turno-escribania">
                                                        Escribanía {turno.horaEscribania}
                                                    </span></>
                                                )}
                                            </div>

                                            <div className="mt-2 d-flex flex-wrap gap-2">
                                                {(turno.presentaciones || []).map(presentacion => {
                                                    const ruta = rutaDelTramite(presentacion);
                                                    return (
                                                        <span key={presentacion.id} className="anses-presentacion">
                                                            <span className="anses-presentacion-tipo">{presentacion.tipoTramite}</span>
                                                            <span className="anses-presentacion-ref">
                                                                {presentacion.idTramite ? `#${presentacion.idTramite}` : 'sin carpeta'}
                                                            </span>
                                                            <span className={`badge rounded-pill ${metaResultado(presentacion.resultado).badge}`}>
                                                                {metaResultado(presentacion.resultado).label}
                                                            </span>
                                                            {/* Acceso al trámite para ver el detalle y registrar el resultado. */}
                                                            {ruta && (
                                                                <button
                                                                    type="button"
                                                                    className="anses-presentacion-link"
                                                                    onClick={() => navigate(ruta)}
                                                                    title="Abrir el trámite"
                                                                >
                                                                    Ver trámite <i className="bi bi-box-arrow-up-right"></i>
                                                                </button>
                                                            )}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="rounded-pill"
                                            onClick={() => setDetalle(turno)}
                                        >
                                            <i className="bi bi-eye me-1"></i>Ver detalle
                                        </Button>
                                        {turno.estado === 'PROGRAMADO' && (
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="rounded-pill"
                                                onClick={() => abrirEdicion(turno)}
                                            >
                                                <i className="bi bi-pencil me-1"></i>Editar
                                            </Button>
                                        )}
                                        {turno.estado === 'PROGRAMADO' && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                className="rounded-pill"
                                                onClick={() => handleCancelar(turno)}
                                                disabled={guardando}
                                            >
                                                Cancelar
                                            </Button>
                                        )}
                                        {/* Cancelar deja el turno a la vista, que es lo correcto
                                            cuando pasó de verdad. Esto es para el que se cargó
                                            por error y solo ensucia el listado. */}
                                        {turno.estado === 'CANCELADO' && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                className="rounded-pill"
                                                onClick={() => handleEliminar(turno)}
                                                disabled={guardando}
                                                title="Borrar definitivamente"
                                            >
                                                <i className="bi bi-trash me-1"></i>Eliminar
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ))
            )}

            {/* Confirmar borrado definitivo */}
            <ModalConfirmacionCambioEstado
                isOpen={!!turnoAEliminar}
                onClose={() => setTurnoAEliminar(null)}
                onConfirm={confirmarEliminar}
                titulo="Eliminar turno ANSES"
                mensaje={turnoAEliminar
                    ? `Se va a borrar definitivamente el turno de ${turnoAEliminar.apellidoCliente}, ${turnoAEliminar.nombreCliente}. Esta acción no se puede deshacer.`
                    : ''}
                cargando={guardando}
                textoBotonConfirmar="Eliminar"
                variantBotonConfirmar="danger"
            />

            {/* Detalle (componente compartido, reutilizado también en la cola de atención) */}
            <ModalDetalleTurnoAnses
                idTurnoAnses={detalle?.idTurnoAnses || null}
                turnoPreload={detalle}
                onClose={() => setDetalle(null)}
            />

            {/* Alta */}
            <Modal
                show={mostrarAlta}
                onHide={() => { setMostrarAlta(false); setEditando(null); }}
                size="lg"
                centered
                scrollable
                className="anses-modal"
                backdropClassName="anses-modal-backdrop"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-building me-2"></i>{editando ? 'Editar turno ANSES' : 'Cargar turno ANSES'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>

                    <div className="anses-seccion">
                        <div className="anses-seccion-titulo">
                            <i className="bi bi-person-vcard"></i>Cliente
                        </div>
                        {datos.cuil ? (
                            <div className="anses-cliente-elegido">
                                <i className="bi bi-check-circle-fill text-success"></i>
                                <div className="flex-grow-1">
                                    <div className="fw-semibold">{datos.nombreCliente}</div>
                                    <small className="text-muted">{datos.cuil}</small>
                                </div>
                                <Button variant="outline-secondary" size="sm" className="rounded-pill"
                                        onClick={() => setDatos({ ...datos, cuil: '', nombreCliente: '' })}>
                                    Cambiar
                                </Button>
                            </div>
                        ) : (
                            <>
                                <Form.Control
                                    placeholder="Buscar por CUIL, nombre o apellido..."
                                    value={busquedaCliente}
                                    onChange={(e) => setBusquedaCliente(e.target.value)}
                                    autoComplete="off"
                                />
                                {buscandoCliente && (
                                    <small className="text-muted d-block mt-1">Buscando...</small>
                                )}
                                {!buscandoCliente && busquedaCliente.trim() && resultadosCliente.length === 0 && (
                                    <small className="text-muted d-block mt-1">Sin resultados.</small>
                                )}
                                {resultadosCliente.length > 0 && (
                                    <ul className="list-group anses-resultado-busqueda">
                                        {resultadosCliente.map(cliente => (
                                            <li
                                                key={cliente.cuil}
                                                className="list-group-item list-group-item-action"
                                                role="button"
                                                onClick={() => setDatos({
                                                    ...datos,
                                                    cuil: cliente.cuil,
                                                    nombreCliente: `${cliente.apellido}, ${cliente.nombre}`
                                                })}
                                            >
                                                {apellidoNombre(cliente.apellido, cliente.nombre)} <span className="text-muted">· {cliente.cuil}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        )}
                    </div>

                    <div className="anses-seccion">
                        <div className="anses-seccion-titulo">
                            <i className="bi bi-folder2-open"></i>Qué se presenta
                        </div>
                        <Row className="g-3">
                        {/* El tipo se elige al crear el turno. Al editar no aparece: el turno ya
                            tiene sus presentaciones y cambiar el tipo acá no hacía nada — el
                            formulario lo pedía y después lo descartaba. Las presentaciones se
                            agregan o quitan desde el trámite. */}
                        {!editando && (
                            <Col md={6}>
                                <Form.Label className="anses-filtros-label">Tipo de trámite</Form.Label>
                                <Form.Select
                                    value={datos.idTipoTramite}
                                    onChange={(e) => setDatos({ ...datos, idTipoTramite: e.target.value })}
                                >
                                    <option value="">Elegí el tipo de trámite...</option>
                                    {tiposTramite.map(tipo => (
                                        <option key={tipo.idTipoTramite} value={tipo.idTipoTramite}>{tipo.nombre}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                        )}
                        {!editando && esReajusteElegido && (
                            <>
                                <Col md={3}>
                                    <Form.Label className="anses-filtros-label">Hora en la escribanía</Form.Label>
                                    <Form.Control
                                        type="time"
                                        value={datos.horaEscribania}
                                        onChange={(e) => setDatos({ ...datos, horaEscribania: e.target.value })}
                                    />
                                    <Form.Text muted>Opcional: si después de ANSES pasa a firmar.</Form.Text>
                                </Col>
                                <Col md={3}>
                                    <Form.Label className="anses-filtros-label">Detalle de la escribanía</Form.Label>
                                    <Form.Control
                                        maxLength={300}
                                        placeholder="Escribanía, dirección, qué firma..."
                                        value={datos.escribaniaObservacion}
                                        onChange={(e) => setDatos({ ...datos, escribaniaObservacion: e.target.value })}
                                    />
                                </Col>
                            </>
                        )}
                        <Col md={6}>
                            <Form.Label className="anses-filtros-label">UDAI / Lugar</Form.Label>
                            <Form.Control
                                list="udai-opciones"
                                value={datos.udaiLugar}
                                onChange={(e) => setDatos({ ...datos, udaiLugar: e.target.value })}
                                placeholder="Elegí o escribí una UDAI..."
                            />
                            <datalist id="udai-opciones">
                                {lugares.map(lugar => <option key={lugar.id} value={lugar.nombre} />)}
                            </datalist>
                        </Col>
                        </Row>
                    </div>

                    <div className="anses-seccion">
                        <div className="anses-seccion-titulo">
                            <i className="bi bi-calendar-event"></i>Cuándo
                        </div>
                        <Row className="g-3">
                        {/* El tipo se define al crear: cambiarlo después tendría implicancias
                            sobre el estado del trámite que no corresponde resolver acá. */}
                        {!editando && (
                        <Col md={12}>
                            <Form.Label className="anses-filtros-label">¿A qué se va?</Form.Label>
                            <div className="d-flex flex-wrap gap-3">
                                {TIPOS_TURNO_ANSES.map((op) => (
                                    <Form.Check
                                        key={op.valor}
                                        type="radio"
                                        name="tipoTurnoAnses"
                                        id={`tipo-${op.valor}`}
                                        label={<><i className={`bi ${op.icono} me-1`}></i>{op.etiqueta}</>}
                                        checked={datos.tipo === op.valor}
                                        onChange={() => setDatos({
                                            ...datos,
                                            tipo: op.valor,
                                            // Ni la vista ni la citación tienen turno dado por el
                                            // organismo. Por eso la hora que se carga es la del estudio,
                                            // el mismo mecanismo que antes movía el checkbox.
                                            tieneTurnoOficial: op.valor === 'PRESENTACION',
                                        })}
                                    />
                                ))}
                            </div>
                            <small className="text-muted d-block mt-1">{metaTipoTurnoAnses(datos.tipo).ayuda}</small>
                        </Col>
                        )}

                        {datos.tipo === 'PRESENTACION' && (
                            <Col md={12}>
                                <Form.Check
                                    type="checkbox"
                                    id="tieneTurnoOficialPagina"
                                    label="Tiene turno dado por ANSES"
                                    checked={datos.tieneTurnoOficial}
                                    onChange={(e) => setDatos({ ...datos, tieneTurnoOficial: e.target.checked })}
                                />
                                <small className="text-muted">
                                    {datos.tieneTurnoOficial
                                        ? 'Se cita al cliente al estudio con la anticipación configurada.'
                                        : 'Sin turno del organismo: la hora que cargues es la del turno en el estudio.'}
                                </small>
                            </Col>
                        )}

                        <Col md={4}>
                            <Form.Label className="anses-filtros-label">Fecha</Form.Label>
                            <Form.Control
                                type="date"
                                value={datos.fecha}
                                onChange={(e) => setDatos({ ...datos, fecha: e.target.value })}
                            />
                            {datos.fecha && datos.fecha < hoyISO() && (
                                <small className="text-warning-emphasis d-block mt-1">
                                    <i className="bi bi-clock-history me-1" />
                                    Turno ya ocurrido: queda como constancia, sin citación en el turnero ni evento de agenda.
                                </small>
                            )}
                        </Col>
                        <Col md={4}>
                            <Form.Label className="anses-filtros-label">
                                {datos.tieneTurnoOficial ? 'Hora en ANSES' : 'Hora en el estudio'}
                            </Form.Label>
                            <Form.Control
                                type="time"
                                value={datos.hora}
                                onChange={(e) => setDatos({ ...datos, hora: e.target.value })}
                            />
                        </Col>
                        {datos.tieneTurnoOficial && (
                            <Col md={4}>
                                <Form.Label className="anses-filtros-label">Anticipación (min)</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    max="480"
                                    placeholder="Por defecto"
                                    value={datos.offsetMinutos}
                                    onChange={(e) => setDatos({ ...datos, offsetMinutos: e.target.value })}
                                />
                            </Col>
                        )}
                        </Row>
                    </div>

                    <div className="anses-seccion">
                        <div className="anses-seccion-titulo">
                            <i className="bi bi-pencil-square"></i>Antes del turno
                        </div>
                        <Row className="g-3">
                        <Col md={12}>
                            <Form.Check
                                type="checkbox"
                                id="requiereFirmaPagina"
                                label="Tiene que firmar documentación antes de ir"
                                checked={datos.requiereFirma}
                                onChange={(e) => setDatos({ ...datos, requiereFirma: e.target.checked })}
                            />
                        </Col>

                        {datos.requiereFirma && (
                            <Col md={12}>
                                <Form.Label className="anses-filtros-label">¿Qué documentación falta firmar?</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={datos.documentacionAFirmar}
                                    onChange={(e) => setDatos({ ...datos, documentacionAFirmar: e.target.value })}
                                    placeholder="Para que recepción sepa qué papel buscar el día del turno..."
                                />
                            </Col>
                        )}
                        </Row>
                    </div>

                    <div className="anses-seccion">
                        <div className="anses-seccion-titulo">
                            <i className="bi bi-person-badge"></i>Quién se presenta
                        </div>
                        <Row className="g-3">
                        {/* Con un abogado del estudio el cliente no viene: no ocupa lugar en la cola. */}
                        <Col md={12}>
                            <Form.Label className="anses-filtros-label">Va un abogado del estudio con poder</Form.Label>
                            <Form.Select
                                value={datos.idUsuarioApoderado}
                                onChange={(e) => setDatos({ ...datos, idUsuarioApoderado: e.target.value })}
                            >
                                <option value="">No — se presenta el cliente</option>
                                {usuarios.map(usuario => (
                                    <option key={usuario.id} value={usuario.id}>{usuario.nombre}</option>
                                ))}
                            </Form.Select>
                            {datos.idUsuarioApoderado && (
                                <div className="anses-nota-poder">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Este turno no genera lugar en el turnero: el cliente no viene al estudio.
                                </div>
                            )}
                        </Col>

                        <Col md={12}>
                            <Form.Label className="anses-filtros-label">Observaciones</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={datos.observaciones}
                                onChange={(e) => setDatos({ ...datos, observaciones: e.target.value })}
                                placeholder="Por ejemplo, el turno posterior en la escribanía..."
                            />
                        </Col>
                        </Row>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" className="rounded-pill px-4"
                            onClick={() => { setMostrarAlta(false); setEditando(null); }} disabled={guardando}>
                        Volver
                    </Button>
                    <Button variant="primary" className="rounded-pill px-4" onClick={handleCrear} disabled={guardando}>
                        {guardando ? 'Guardando...' : 'Cargar turno'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default TurnosAnsesPage;
